var VISIBILITY_MINUTES = 5

export default {
  data() {
    return {
      state: 'INITIAL',
      sending: false,
      verifying: false,
      canResend: false,
      hideResend: false,
      codeExhausted: false,
      otpErrorMessage: '',
      deliveryMethod: '',
      resendCountdown: 30,
      resendTimer: null,
      cardDetails: {},
      cardAddress: null,
      loadingAddress: false,
      timeLeft: VISIBILITY_MINUTES * 60,
      visibilityTimer: null,
      visibilityChangeHandler: null,
      warningShown: false,
      revealedAt: 0
    }
  },
  computed: {
    revealed() {
      return this.state === 'REVEALED'
    },
    formattedCardNumber() {
      var num = this.cardDetails.CardNumber || ''
      return num.replace(/(.{4})/g, '$1 ').trim()
    },
    otpInstructionMessage() {
      return 'To see your card number click the button and you will receive a one-time passcode via text message or email.'
    },
    otpPasscodeHint() {
      if (this.deliveryMethod === 'sms') {
        return 'Enter the one-time passcode that you received via text message.'
      }
      if (this.deliveryMethod === 'email') {
        return 'Enter the one-time passcode that you received via email.'
      }
      return 'Enter the one-time passcode that you received.'
    }
  },
  beforeDestroy() {
    this.clearTimers()
  },
  methods: {
    // Override in component to return the card ID
    getCardId() {
      throw new Error('getCardId() must be implemented by the component')
    },

    // Override in component to return the 6-digit passcode string
    getPasscode() {
      throw new Error('getPasscode() must be implemented by the component')
    },

    // Override in component to clear the passcode input
    resetPasscodeInput() {
      // default no-op, override in component
    },

    // Override in component to return the account ID for address fetch
    getAccountId() {
      return null
    },

    async fetchAddress(accountId) {
      if (accountId) {
        this.loadingAddress = true
        try {
          var response = await this.api.get(`evedpaycards/accounts/${accountId}/get-address`)
          if (response.status === 200) {
            this.cardAddress = response.data
          }
        } catch (error) {
          console.error(error)
        } finally {
          this.loadingAddress = false
        }
      }
    },

    async sendOtp() {
      if (!this.getCardId()) { return }
      this.sending = true
      try {
        var response = await this.api.post(`${this.api.$.evedpaycard.requestCardOtp}/${this.getCardId()}`, { preventError: true })
        if (response.data && response.data.DeliveryMethod) {
          this.deliveryMethod = response.data.DeliveryMethod
        }
        this.state = 'ENTER_CODE'
        this.resetPasscodeInput()
        this.codeExhausted = false
        this.hideResend = false
        this.otpErrorMessage = ''
        this.startResendCooldown()
        this.focusPasscodeInput()
      } catch (error) {
        var message = this.getErrorMessage(error)
        if (error.response && error.response.status === 429 && message.indexOf('15 minutes') !== -1) {
          if (this.state === 'ENTER_CODE') {
            this.otpErrorMessage = message
            this.hideResend = true
            this.codeExhausted = true
          } else {
            this.showError(error)
          }
        } else {
          this.showError(error)
        }
      } finally {
        this.sending = false
      }
    },

    // Override in component to focus the passcode input (called after send and after verify failure)
    focusPasscodeInput() {},

    resendOtp() {
      if (this.canResend) {
        this.sendOtp()
      }
    },

    startResendCooldown() {
      this.canResend = false
      this.resendCountdown = 30
      clearInterval(this.resendTimer)
      this.resendTimer = setInterval(() => {
        this.resendCountdown--
        if (this.resendCountdown <= 0) {
          this.canResend = true
          clearInterval(this.resendTimer)
        }
      }, 1000)
    },

    async verifyOtp() {
      var passcode = this.getPasscode()
      if (passcode.length < 6) {
        return
      }
      this.verifying = true
      try {
        var response = await this.api.post(`${this.api.$.evedpaycard.verifyCardOtp}/${this.getCardId()}`, {
          code: passcode,
          preventError: true
        })
        this.cardDetails = response.data
        this.state = 'REVEALED'
        this.fetchAddress(this.getAccountId())
        this.startVisibilityTimer()
      } catch (error) {
        var message = this.getErrorMessage(error)
        if (error.response && error.response.status === 429) {
          if (message.indexOf('Resend Code') !== -1) {
            this.codeExhausted = true
            this.otpErrorMessage = message
            this.canResend = true
            this.hideResend = false
            clearInterval(this.resendTimer)
          } else {
            this.otpErrorMessage = message
            this.hideResend = true
            this.codeExhausted = true
          }
        } else {
          this.showError(error)
          if (error.response && error.response.status === 410) {
            this.state = 'INITIAL'
          }
        }
        this.resetPasscodeInput()
        this.focusPasscodeInput()
      } finally {
        this.verifying = false
      }
    },

    startVisibilityTimer() {
      clearInterval(this.visibilityTimer)
      // Defensive: remove any existing listener before attaching a new one
      if (this.visibilityChangeHandler) {
        document.removeEventListener('visibilitychange', this.visibilityChangeHandler)
        this.visibilityChangeHandler = null
      }
      this.revealedAt = Date.now()
      this.timeLeft = VISIBILITY_MINUTES * 60
      this.warningShown = false

      // Hide immediately when the tab becomes visible again if the timeout already elapsed.
      // setInterval is throttled/paused in inactive tabs, so it cannot be relied on alone.
      this.visibilityChangeHandler = () => {
        if (document.visibilityState === 'visible') {
          var elapsed = Math.floor((Date.now() - this.revealedAt) / 1000)
          if (elapsed >= VISIBILITY_MINUTES * 60) {
            clearInterval(this.visibilityTimer)
            this.resetOtpState()
          }
        }
      }
      document.addEventListener('visibilitychange', this.visibilityChangeHandler)

      this.visibilityTimer = setInterval(() => {
        var elapsed = Math.floor((Date.now() - this.revealedAt) / 1000)
        this.timeLeft = Math.max(0, VISIBILITY_MINUTES * 60 - elapsed)
        if (this.timeLeft <= 120 && !this.warningShown && this.timeLeft > 0) {
          this.warningShown = true
          this.$notifications.notify({
            message: 'Card details will be hidden soon.',
            type: 'plain',
            icon: 'exclamation-triangle',
            timeout: 5000,
            placement: { from: 'top', align: 'center' }
          })
        }
        if (this.timeLeft <= 0) {
          clearInterval(this.visibilityTimer)
          this.resetOtpState()
          this.$notifications.notify({
            message: 'Card details hidden — time expired.',
            type: 'plain',
            icon: 'times-octagon',
            timeout: 5000,
            placement: { from: 'top', align: 'center' }
          })
        }
      }, 1000)
    },

    resetOtpState() {
      this.clearTimers()
      this.state = 'INITIAL'
      this.cardDetails = {}
      this.cardAddress = null
      this.resetPasscodeInput()
      this.canResend = false
      this.hideResend = false
      this.codeExhausted = false
      this.otpErrorMessage = ''
      this.deliveryMethod = ''
    },

    copyCardNumber() {
      var num = this.cardDetails.CardNumber || ''
      navigator.clipboard.writeText(num).then(() => {
        this.$notifications.notify({
          message: 'Card number copied.',
          type: 'info',
          timeout: 2000,
          placement: { from: 'top', align: 'center' }
        })
      }).catch(() => {
        this.$notifications.notify({
          message: 'Unable to copy. Please copy manually.',
          type: 'info',
          timeout: 2000,
          placement: { from: 'top', align: 'center' }
        })
      })
    },

    clearTimers() {
      clearInterval(this.resendTimer)
      clearInterval(this.visibilityTimer)
      if (this.visibilityChangeHandler) {
        document.removeEventListener('visibilitychange', this.visibilityChangeHandler)
        this.visibilityChangeHandler = null
      }
    },

    getErrorMessage(error) {
      if (error.response && error.response.data && error.response.data.length) {
        return error.response.data[0].Message || ''
      }
      return ''
    },

    showError(error) {
      var message = 'Something went wrong. Please try again.'
      var extracted = this.getErrorMessage(error)
      if (extracted) {
        message = extracted
      } else if (!error.response) {
        message = "We couldn't verify the code. Check your connection and try again."
      }
      this.$notifications.notify({
        message: message,
        type: 'plain',
        icon: 'times-octagon',
        timeout: 5000,
        placement: { from: 'top', align: 'center' }
      })
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/_modules/EvedPay/otpCardRevealMixin.js